import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, ChoiceControl, DataTable, DetailList, Dialog, Field, Pagination, StatusBadge } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { failure } from "../../workspace/behavior";
import { businessCodeLabel, date } from "../../workspace/model";
import { FilterBar, PageHeader, Panel, RowAction, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import type { AdminUserMutation } from "../queries";
import {
  ADMIN_ROLE_CODES,
  adminRolesSchema,
  createAdminSchema,
  disableAdminSchema,
  isAdminRoleCode,
  resetAdminTotpSchema,
} from "../schema";
import type { AdminRoleCode, AdminRoleFilter, AdminUserCriteria, AdminUserStatusFilter, CreateAdminValues } from "../schema";

type QueryState = "ready" | "loading" | "error";
type Editor =
  | { readonly kind: "create"; readonly values: CreateAdminValues }
  | { readonly kind: "roles"; readonly user: Schemas["AdminUserListItemResponse"]; readonly roleCodes: readonly AdminRoleCode[] }
  | { readonly kind: "disable"; readonly user: Schemas["AdminUserListItemResponse"]; readonly reason: string }
  | { readonly kind: "totp"; readonly user: Schemas["AdminUserListItemResponse"]; readonly reauthenticationPassword: string };

const roleLabels: Readonly<Record<AdminRoleCode, string>> = {
  SUPER: "시스템 관리자",
  OPERATOR: "운영 담당자",
  CS: "고객지원 담당자",
  VIEWER: "조회 담당자",
};

const createValues = (): CreateAdminValues => ({
  loginId: "",
  name: "",
  email: "",
  initialPassword: "",
  roleCodes: ["OPERATOR"],
});

function selectedRoles(values: readonly string[]) {
  return values.filter(isAdminRoleCode);
}

function managementFailure(cause: unknown) {
  const code = (cause as { code?: string } | null)?.code;
  const mapped: Readonly<Record<string, { readonly message: string; readonly reconcile: boolean }>> = {
    ADMIN_LOGIN_ID_DUPLICATED: { message: "이미 사용 중인 로그인 계정입니다.", reconcile: false },
    ADMIN_PASSWORD_POLICY_VIOLATION: { message: "초기 비밀번호 정책을 확인해 주세요.", reconcile: false },
    ADMIN_ROLE_REQUIRED: { message: "역할을 하나 이상 선택해 주세요.", reconcile: false },
    ADMIN_ROLE_INVALID: { message: "지원하지 않는 관리자 역할이 포함되어 있습니다.", reconcile: false },
    LAST_SUPER_PROTECTED: { message: "마지막 활성 SUPER의 역할을 제거하거나 계정을 비활성화할 수 없습니다.", reconcile: true },
    ADMIN_SELF_DISABLE_DENIED: { message: "현재 로그인한 관리자 계정은 비활성화할 수 없습니다.", reconcile: false },
    ADMIN_REAUTHENTICATION_REQUIRED: { message: "현재 관리자 비밀번호가 일치하지 않습니다.", reconcile: false },
  };
  return code && mapped[code] ? { ...failure(cause), ...mapped[code] } : failure(cause);
}

function mutationFor(editor: Editor): AdminUserMutation | null {
  if (editor.kind === "create") {
    const parsed = createAdminSchema.safeParse(editor.values);
    if (!parsed.success) return null;
    return {
      kind: "create",
      body: {
        loginId: parsed.data.loginId,
        name: parsed.data.name,
        email: parsed.data.email || null,
        initialPassword: parsed.data.initialPassword,
        roleCodes: parsed.data.roleCodes,
      },
    };
  }
  if (editor.kind === "roles") return { kind: "roles", adminUserId: editor.user.id, body: { roleCodes: [...editor.roleCodes] } };
  if (editor.kind === "disable") return { kind: "disable", adminUserId: editor.user.id, body: { reason: editor.reason.trim() } };
  return { kind: "totp", adminUserId: editor.user.id, body: { reauthenticationPassword: editor.reauthenticationPassword } };
}

export interface AdminUsersViewProps {
  readonly criteria: AdminUserCriteria;
  readonly result?: Schemas["AdminUserPageResponse"];
  readonly listState: QueryState;
  readonly selectedId?: number;
  readonly selected?: Schemas["AdminUserListItemResponse"];
  readonly currentAdminId?: number;
  readonly canManage: boolean;
  readonly notice?: { readonly tone: "information" | "success"; readonly text: string };
  readonly onSearch: (criteria: AdminUserCriteria) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (adminUserId: number) => void;
  readonly onClearSelection: () => void;
  readonly onRetryList: () => void | Promise<unknown>;
  readonly onMutation: (input: AdminUserMutation) => Promise<unknown>;
  readonly onMutationComplete: (input: AdminUserMutation, result: unknown) => Promise<void>;
}

export function AdminUsersView(props: AdminUsersViewProps) {
  const [filters, setFilters] = useState({ status: props.criteria.status, roleCode: props.criteria.roleCode });
  const [editor, setEditor] = useState<Editor>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState("");
  const [requestBlocked, setRequestBlocked] = useState(false);
  const [pending, setPending] = useState(false);
  const refs = useRef<Record<string, FieldElement | null>>({});
  const formId = useId();

  useEffect(() => {
    setFilters({ status: props.criteria.status, roleCode: props.criteria.roleCode });
  }, [props.criteria.roleCode, props.criteria.status]);

  useEffect(() => {
    setEditor(undefined);
    setErrors({});
    setRequestError("");
    setRequestBlocked(false);
  }, [props.selectedId]);

  const setRef = (key: string) => (element: FieldElement | null) => { refs.current[key] = element; };
  const resetEditorFeedback = () => { setErrors({}); setRequestError(""); setRequestBlocked(false); };
  const openEditor = (next: Editor) => { resetEditorFeedback(); setEditor(next); };
  const closeEditor = () => {
    if (pending) return;
    setEditor(undefined);
    resetEditorFeedback();
  };
  const updateCreate = (key: keyof Omit<CreateAdminValues, "roleCodes">, value: string) => {
    setEditor((current) => current?.kind === "create" ? { ...current, values: { ...current.values, [key]: value } } : current);
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const toggleRole = (role: AdminRoleCode, checked: boolean) => {
    setEditor((current) => {
      if (!current || (current.kind !== "create" && current.kind !== "roles")) return current;
      const roleCodes = current.kind === "create" ? current.values.roleCodes : current.roleCodes;
      const next = checked ? [...new Set([...roleCodes, role])] : roleCodes.filter((value) => value !== role);
      return current.kind === "create"
        ? { ...current, values: { ...current.values, roleCodes: next } }
        : { ...current, roleCodes: next };
    });
    setErrors((current) => ({ ...current, roleCodes: "" }));
  };
  const showValidation = (issues: readonly { readonly path: PropertyKey[]; readonly message: string }[]) => {
    const next: Record<string, string> = {};
    for (const issue of issues) next[String(issue.path[0])] ??= issue.message;
    setErrors(next);
    refs.current[String(issues[0]?.path[0])]?.focus();
  };

  const submit = async () => {
    if (!editor || pending || requestBlocked) return;
    const parsed = editor.kind === "create"
      ? createAdminSchema.safeParse(editor.values)
      : editor.kind === "roles"
        ? adminRolesSchema.safeParse({ roleCodes: editor.roleCodes })
        : editor.kind === "disable"
          ? disableAdminSchema.safeParse({ reason: editor.reason })
          : resetAdminTotpSchema.safeParse({ reauthenticationPassword: editor.reauthenticationPassword });
    if (!parsed.success) {
      showValidation(parsed.error.issues);
      return;
    }
    const input = mutationFor(editor);
    if (!input) return;
    setPending(true);
    setRequestError("");
    try {
      const result = await props.onMutation(input);
      await props.onMutationComplete(input, result);
      setEditor(undefined);
    } catch (cause) {
      const feedback = managementFailure(cause);
      setRequestError(feedback.message);
      setRequestBlocked(feedback.reconcile);
      if (editor.kind === "create") {
        setEditor({ ...editor, values: { ...editor.values, initialPassword: "" } });
        queueMicrotask(() => refs.current.initialPassword?.focus());
      }
      if (editor.kind === "totp") {
        setEditor({ ...editor, reauthenticationPassword: "" });
        queueMicrotask(() => refs.current.reauthenticationPassword?.focus());
      }
    } finally {
      setPending(false);
    }
  };

  const reconcile = async () => {
    try {
      await props.onRetryList();
      setEditor(undefined);
      resetEditorFeedback();
    } catch (cause) {
      setRequestError(failure(cause).message);
    }
  };
  const retryList = () => { void Promise.resolve(props.onRetryList()).catch(() => undefined); };

  const columns = [
    { key: "login", title: "계정", render: (row: Schemas["AdminUserListItemResponse"]) => <span>{row.loginId}{row.id === props.currentAdminId ? " (현재 계정)" : ""}</span> },
    { key: "name", title: "이름", render: (row: Schemas["AdminUserListItemResponse"]) => row.name },
    { key: "roles", title: "역할", render: (row: Schemas["AdminUserListItemResponse"]) => row.roleCodes.map(businessCodeLabel).join(", ") },
    { key: "totp", title: "TOTP", render: (row: Schemas["AdminUserListItemResponse"]) => <StatusBadge variant={row.totpEnrolled ? "success" : "waiting"}>{row.totpEnrolled ? "등록됨" : "등록 필요"}</StatusBadge> },
    { key: "status", title: "상태", render: (row: Schemas["AdminUserListItemResponse"]) => statusBadge(row.status) },
    { key: "last", title: "최근 로그인", render: (row: Schemas["AdminUserListItemResponse"]) => date(row.lastLoginAt) },
    { key: "action", title: "작업", render: (row: Schemas["AdminUserListItemResponse"]) => <RowAction onClick={() => props.onSelect(row.id)}>관리자 {row.id} 선택</RowAction> },
  ] satisfies readonly DataColumn<Schemas["AdminUserListItemResponse"]>[];

  const dialogTitle = editor?.kind === "create" ? "관리자 계정을 추가할까요?"
    : editor?.kind === "roles" ? "관리자 역할을 변경할까요?"
      : editor?.kind === "disable" ? "관리자 계정을 비활성화할까요?"
        : "관리자 TOTP를 초기화할까요?";
  const confirmLabel = editor?.kind === "create" ? "관리자 추가"
    : editor?.kind === "roles" ? "역할 저장"
      : editor?.kind === "disable" ? "계정 비활성화"
        : "TOTP 초기화";
  const target = editor && editor.kind !== "create" ? `${editor.user.name} · ${editor.user.loginId}` : undefined;

  return <>
    <PageHeader id="ADM-S21" title="관리자 계정" description="관리자 계정, 역할과 TOTP 등록 상태를 관리합니다." meta={<p aria-live="polite">현재 조건 {props.result?.totalCount.toLocaleString("ko-KR") ?? 0}건</p>} action={<GuidedActionButton size="small" blockedReason={props.canManage ? null : "시스템 관리자 역할과 관리자 관리 권한이 필요합니다. 권한 관리자에게 요청해 주세요."} onClick={() => openEditor({ kind: "create", values: createValues() })}>관리자 추가</GuidedActionButton>} />
    {!props.canManage ? <Alert persistent variant="warning" title="시스템 관리자 권한이 필요합니다">ROLE_SUPER와 ADMIN_MANAGE 권한이 모두 있어야 관리자 계정을 관리할 수 있습니다.</Alert> : null}
    {props.notice ? <Alert persistent variant={props.notice.tone} title="관리자 계정 처리 결과">{props.notice.text}</Alert> : null}

    <Panel title="관리자 계정 검색" description="상태와 역할을 서버 조회 조건으로 적용합니다.">
      <FilterBar onSubmit={() => props.onSearch({ ...props.criteria, ...filters, page: 0 })}>
        <Field compact control="select" label="계정 상태" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.currentTarget.value as AdminUserStatusFilter }))}><option value="">전체</option><option value="ACTIVE">활성</option><option value="DISABLED">비활성</option></Field>
        <Field compact control="select" label="역할" value={filters.roleCode} onChange={(event) => setFilters((current) => ({ ...current, roleCode: event.currentTarget.value as AdminRoleFilter }))}><option value="">전체</option>{ADMIN_ROLE_CODES.map((role) => <option key={role} value={role}>{roleLabels[role]}</option>)}</Field>
      </FilterBar>
      <DataTable caption="관리자 계정" columns={columns} rows={props.result?.items ?? []} rowKey={(row) => row.id} state={props.listState} onRetry={retryList} />
      {props.result ? <Pagination page={props.result.page} size={props.result.size} totalCount={props.result.totalCount} onPageChange={props.onPageChange} /> : null}
    </Panel>

    {props.selected ? <Panel title={props.selected.name} description={`로그인 ID ${props.selected.loginId}`} action={<Button size="small" variant="quiet" onClick={props.onClearSelection}>선택 닫기</Button>}>
      <DetailList items={[
        { label: "상태", value: statusBadge(props.selected.status) },
        { label: "역할", value: props.selected.roleCodes.map(businessCodeLabel).join(", ") },
        { label: "이메일", value: props.selected.email || "미등록" },
        { label: "TOTP", value: props.selected.totpEnrolled ? "등록됨" : "등록 필요" },
        { label: "최초 비밀번호 변경", value: props.selected.forcePasswordChange ? "필요" : "완료" },
        { label: "최근 로그인", value: date(props.selected.lastLoginAt) },
        { label: "생성일시", value: date(props.selected.createdAt) },
      ]} />
      {props.selected.id === props.currentAdminId ? <Alert persistent variant="information" title="현재 로그인한 계정입니다">역할 변경 또는 TOTP 초기화가 성공하면 기존 세션이 폐기되어 다시 로그인해야 합니다. 현재 계정은 비활성화할 수 없습니다.</Alert> : null}
      <div className="admin-detail-actions">
        <GuidedActionButton size="small" variant="secondary" blockedReason={props.canManage ? null : "관리자 관리 권한이 필요합니다. 권한 관리자에게 요청해 주세요."} onClick={() => openEditor({ kind: "roles", user: props.selected!, roleCodes: selectedRoles(props.selected!.roleCodes) })}>역할 변경</GuidedActionButton>
        <GuidedActionButton size="small" variant="secondary" blockedReason={!props.canManage ? "관리자 관리 권한이 필요합니다. 권한 관리자에게 요청해 주세요." : !props.selected.totpEnrolled ? "이 계정은 TOTP를 등록하지 않았습니다. 최초 로그인에서 TOTP를 등록한 뒤 초기화할 수 있습니다." : null} onClick={() => openEditor({ kind: "totp", user: props.selected!, reauthenticationPassword: "" })}>TOTP 초기화</GuidedActionButton>
        <GuidedActionButton size="small" variant="danger" blockedReason={!props.canManage ? "관리자 관리 권한이 필요합니다. 권한 관리자에게 요청해 주세요." : props.selected.id === props.currentAdminId ? "현재 로그인한 계정은 비활성화할 수 없습니다. 다른 관리자 계정으로 처리해 주세요." : props.selected.status !== "ACTIVE" ? "이미 비활성화된 계정입니다." : null} onClick={() => openEditor({ kind: "disable", user: props.selected!, reason: "" })}>계정 비활성화</GuidedActionButton>
      </div>
    </Panel> : null}

    <Dialog open={Boolean(editor)} title={dialogTitle} description={target ?? "생성한 계정은 최초 로그인에서 비밀번호 변경과 TOTP 등록이 필요합니다."} closeDisabled={pending} onClose={closeEditor} size="medium"
      secondaryAction={<Button size="small" variant="quiet" disabled={pending} onClick={closeEditor}>취소</Button>}
      primaryAction={<GuidedActionButton size="small" type="submit" form={formId} variant={editor?.kind === "disable" ? "danger" : "primary"} isLoading={pending} blockedReason={!props.canManage ? "관리자 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요." : requestBlocked ? "직전 요청의 처리 결과가 확정되지 않았습니다. 목록 다시 조회를 눌러 최신 상태를 확인해 주세요." : null}>{confirmLabel}</GuidedActionButton>}>
      {requestError ? <Alert persistent variant="danger" title={requestBlocked ? "최신 상태 확인 필요" : "요청을 처리하지 못했습니다"} action={requestBlocked ? <Button size="small" variant="quiet" onClick={() => void reconcile()}>목록 다시 조회</Button> : undefined}>{requestError}</Alert> : null}
      {editor ? <form className="admin-command" id={formId} noValidate onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        {editor.kind === "create" ? <>
          <Field ref={setRef("loginId")} compact label="로그인 계정" required maxLength={100} value={editor.values.loginId} error={errors.loginId} onChange={(event) => updateCreate("loginId", event.currentTarget.value)} />
          <Field ref={setRef("name")} compact label="이름" required maxLength={100} value={editor.values.name} error={errors.name} onChange={(event) => updateCreate("name", event.currentTarget.value)} />
          <Field ref={setRef("email")} compact label="이메일" type="email" maxLength={255} value={editor.values.email} error={errors.email} onChange={(event) => updateCreate("email", event.currentTarget.value)} />
          <Field ref={setRef("initialPassword")} compact autoComplete="new-password" label="초기 비밀번호" required type="password" maxLength={30} value={editor.values.initialPassword} error={errors.initialPassword} description="8~30자이며 영문 대문자·소문자·숫자·특수문자를 모두 포함합니다." onChange={(event) => updateCreate("initialPassword", event.currentTarget.value)} />
        </> : null}
        {editor.kind === "create" || editor.kind === "roles" ? <fieldset className="admin-choice-group">
          <legend>역할</legend>
          {ADMIN_ROLE_CODES.map((role, index) => {
            const values = editor.kind === "create" ? editor.values.roleCodes : editor.roleCodes;
            return <ChoiceControl key={role} ref={index === 0 ? setRef("roleCodes") : undefined} checked={values.includes(role)} label={roleLabels[role]} value={role} error={index === 0 ? errors.roleCodes : undefined} onChange={(event) => toggleRole(role, event.currentTarget.checked)} />;
          })}
        </fieldset> : null}
        {editor.kind === "disable" ? <><Alert persistent variant="warning" title="즉시 세션이 폐기됩니다">비활성화한 계정은 로그인할 수 없습니다. 마지막 활성 SUPER는 서버에서 보호됩니다.</Alert><Field ref={setRef("reason")} compact control="textarea" label="비활성화 사유" required maxLength={500} value={editor.reason} error={errors.reason} onChange={(event) => { setEditor({ ...editor, reason: event.currentTarget.value }); setErrors((current) => ({ ...current, reason: "" })); }} /></> : null}
        {editor.kind === "totp" ? <><Alert persistent variant="warning" title="현재 관리자의 재인증이 필요합니다">대상 계정의 TOTP 등록과 세션이 폐기됩니다. 재인증 비밀번호는 저장하지 않습니다.</Alert><Field ref={setRef("reauthenticationPassword")} compact autoComplete="current-password" label="현재 관리자 비밀번호" required type="password" maxLength={200} value={editor.reauthenticationPassword} error={errors.reauthenticationPassword} onChange={(event) => { setEditor({ ...editor, reauthenticationPassword: event.currentTarget.value }); setErrors((current) => ({ ...current, reauthenticationPassword: "" })); }} /></> : null}
      </form> : null}
    </Dialog>
  </>;
}
